import type { PluginManifest, ResolvedPluginManifest, InitPayload } from "@aspect/plugin-sdk";

import { STORAGE_KEYS } from "@/shared/constants/storage-keys";

import { UrlMatcher } from "./url-matcher";
import { loadWorker } from "./worker-loader";
import { HostBridge } from "../bridge/host-bridge";
import { MessageRouter } from "../bridge/message-router";
import { resolveManifest } from "../registry/manifest-resolver";
import { clearPluginContextMenuItems } from "../services/context-menu-service";
import { clearPluginThemeEffects, restorePluginBackground } from "../services/theme-service";
import { usePluginStore } from "../state/plugin-store";


/** 宿主内置的插件 SDK 版本号，用于与 manifest 的 `sdk` 字段做兼容性校验 */
const SDK_VERSION = "1.0.0";

/**
 * 需要同步到插件端的设计令牌（CSS 变量名）。
 * 与宿主主题（如 shadcn）对齐，使插件 UI 与主应用使用同一套颜色与圆角等视觉变量。
 */
const CSS_VAR_NAMES = [
  "--radius", "--background", "--foreground",
  "--card", "--card-foreground",
  "--popover", "--popover-foreground",
  "--primary", "--primary-foreground",
  "--secondary", "--secondary-foreground",
  "--muted", "--muted-foreground",
  "--accent", "--accent-foreground",
  "--destructive", "--border", "--input", "--ring",
];

/**
 * 从宿主根元素（`document.documentElement`）读取上述 CSS 变量的**计算后**值。
 * 供初始化插件时注入 `InitPayload.cssVariables`，使 Worker/iframe 内样式与宿主一致。
 */
export function collectCssVariables(): Record<string, string> {
  const style = getComputedStyle(document.documentElement);
  const vars: Record<string, string> = {};
  for (const name of CSS_VAR_NAMES) {
    const val = style.getPropertyValue(name).trim();
    if (val) vars[name] = val;
  }
  return vars;
}

/**
 * 校验 manifest 要求的 SDK 范围是否与当前宿主兼容。
 * 仅比较**主版本号**：当前主版本不低于要求的主版本即视为满足（例如 `^1` 与 `1.0.0`）。
 */
function satisfiesSdkVersion(required: string, current: string): boolean {
  const match = required.match(/^\^?(\d+)/);
  if (!match) return false;
  const requiredMajor = parseInt(match[1], 10);
  const currentMajor = parseInt(current.split(".")[0], 10);
  return currentMajor >= requiredMajor;
}

/**
 * 插件宿主侧的核心生命周期管理器：注册 manifest、激活/停用 Worker 与 iframe、
 * 与 {@link HostBridge} 协同完成 RPC，并向 Zustand store 同步路由/工具栏等贡献点状态。
 */
export class PluginManager {
  readonly bridge: HostBridge;
  readonly router: MessageRouter;
  readonly urlMatcher: UrlMatcher;

  private manifests = new Map<string, ResolvedPluginManifest>();
  private baseUrls = new Map<string, string>();
  private workers = new Map<string, Worker>();
  private activeIframes = new Map<string, Set<HTMLIFrameElement>>();

  constructor() {
    this.bridge = new HostBridge();
    this.router = new MessageRouter();
    this.urlMatcher = new UrlMatcher();

    this.bridge.setRpcHandler((pluginId, method, params) => {
      return this.router.route(pluginId, method, params);
    });
  }

  /**
   * 解析并校验 manifest（含 SDK 版本），写入内存映射并在 store 中注册插件元数据。
   * 不创建 Worker/iframe；激活见 {@link activatePlugin}。
   */
  async loadPlugin(rawManifest: PluginManifest, baseUrl: string): Promise<void> {
    const manifest = resolveManifest(rawManifest);

    if (!satisfiesSdkVersion(manifest.sdk, SDK_VERSION)) {
      throw new Error(
        `Plugin "${manifest.name}" requires SDK ${manifest.sdk}, but host supports ${SDK_VERSION}`
      );
    }

    this.manifests.set(manifest.id, manifest);
    this.baseUrls.set(manifest.id, baseUrl);

    const store = usePluginStore.getState();
    store.registerPlugin(manifest, baseUrl);
  }

  /**
   * 激活插件：若 manifest 含 `main` 则创建 Worker、注册到 Bridge 并发送初始化 payload；
   * 注册工具栏/路由/URL 匹配等贡献（iframe 由宿主页面创建后通过 {@link registerIframe} 接入 Bridge）；
   * 按需恢复主题背景；将状态置为 active 并持久化为「已启用」。
   */
  async activatePlugin(pluginId: string): Promise<void> {
    const manifest = this.manifests.get(pluginId);
    const baseUrl = this.baseUrls.get(pluginId);
    if (!manifest || !baseUrl) return;

    const store = usePluginStore.getState();

    try {
      if (manifest.main) {
        const mainUrl = this.resolveUrl(baseUrl, manifest.main);
        const allowedDomains = manifest.networkAccess?.allowedDomains ?? [];
        const worker = await loadWorker(mainUrl, allowedDomains);

        this.workers.set(pluginId, worker);
        this.bridge.registerWorker(pluginId, worker);
        this.bridge.sendInit(pluginId, this.createInitPayload(pluginId));
      }

      if (manifest.toolbar) {
        store.registerToolbarButton(pluginId, manifest.toolbar);
      }
      if (manifest.route) {
        store.registerRouteEntry(pluginId, manifest.route);
      }
      if (manifest.viewer) {
        this.urlMatcher.register(pluginId, manifest.viewer.urlPatterns);
      }

      if (manifest.permissions?.includes("theme:write")) {
        restorePluginBackground();
      }

      store.setPluginStatus(pluginId, "active");
      this.persistPluginEnabled(pluginId, true);
    } catch (err) {
      store.setPluginStatus(pluginId, "error", err instanceof Error ? err.message : String(err));
      throw err;
    }
  }

  /**
   * 将插件对应的 iframe 注册到 Bridge 的消息通道，并记录在 `activeIframes` 中，
   * 便于后续对该插件的所有 iframe 广播初始化等消息。
   *
   * @param origin `postMessage` 目标 origin，默认 `*`（与 Bridge 行为一致）
   */
  registerIframe(
    pluginId: string,
    iframe: HTMLIFrameElement,
    origin: string = "*"
  ): void {
    this.bridge.registerIframe(pluginId, iframe, origin);

    if (!this.activeIframes.has(pluginId)) {
      this.activeIframes.set(pluginId, new Set());
    }
    this.activeIframes.get(pluginId)!.add(iframe);
  }

  /**
   * 向该插件已注册的 Worker/iframe 通道发送初始化 payload。
   * `extraPayload` 会与 {@link createInitPayload} 的结果浅合并，用于覆盖主题、权限等字段。
   */
  sendInitToIframe(pluginId: string, extraPayload?: Partial<InitPayload>): void {
    const payload = { ...this.createInitPayload(pluginId), ...extraPayload };
    this.bridge.sendInit(pluginId, payload);
  }

  /**
   * 停用插件：终止 Worker、从 Bridge 注销、清理 iframe 追踪与上下文菜单/URL 匹配；
   * 移除 store 中的工具栏与路由贡献；若曾持有 `theme:write` 则清理主题副作用；
   * 状态回退为 `loaded`，并持久化为「未启用」（不删除 manifest）。
   */
  async deactivatePlugin(pluginId: string): Promise<void> {
    const worker = this.workers.get(pluginId);
    if (worker) {
      worker.terminate();
      this.workers.delete(pluginId);
    }

    this.bridge.unregisterPlugin(pluginId);
    this.activeIframes.delete(pluginId);
    clearPluginContextMenuItems(pluginId);
    this.urlMatcher.unregister(pluginId);

    const manifest = this.manifests.get(pluginId);
    if (manifest?.permissions?.includes("theme:write")) {
      clearPluginThemeEffects();
    }

    const store = usePluginStore.getState();
    store.removeToolbarButton(pluginId);
    store.removeRouteEntry(pluginId);
    store.setPluginStatus(pluginId, "loaded");
    this.persistPluginEnabled(pluginId, false);
  }

  /**
   * 完全卸载插件：先 {@link deactivatePlugin}，再移除 manifest/baseUrl 与 store 中的插件记录。
   * 适用于用户卸载或宿主不再需要该插件的场景。
   */
  async unloadPlugin(pluginId: string): Promise<void> {
    await this.deactivatePlugin(pluginId);
    this.manifests.delete(pluginId);
    this.baseUrls.delete(pluginId);
    this.urlMatcher.unregister(pluginId);

    const store = usePluginStore.getState();
    store.removePlugin(pluginId);
  }

  getManifest(pluginId: string): ResolvedPluginManifest | undefined {
    return this.manifests.get(pluginId);
  }

  getBaseUrl(pluginId: string): string | undefined {
    return this.baseUrls.get(pluginId);
  }

  resolveUrl(baseUrl: string, path: string): string {
    const normalizedBase = baseUrl.endsWith("/") ? baseUrl : baseUrl + "/";
    const normalizedPath = path.startsWith("./") ? path.slice(2) : path;
    const absoluteBase = /^https?:\/\//.test(normalizedBase)
      ? normalizedBase
      : new URL(normalizedBase, window.location.origin).href;
    return new URL(normalizedPath, absoluteBase).href;
  }

  /**
   * 从 localStorage（`STORAGE_KEYS.pluginStates`）读取该插件是否被用户关闭；
   * 无记录或解析失败时视为启用（新插件默认启用）。
   */
  isPluginEnabled(pluginId: string): boolean {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.pluginStates);
      if (!raw) return true;
      const states: Record<string, boolean> = JSON.parse(raw);
      return states[pluginId] !== false;
    } catch {
      return true;
    }
  }

  private persistPluginEnabled(pluginId: string, enabled: boolean): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.pluginStates);
      const states: Record<string, boolean> = raw ? JSON.parse(raw) : {};
      states[pluginId] = enabled;
      localStorage.setItem(STORAGE_KEYS.pluginStates, JSON.stringify(states));
    } catch {
      // localStorage 不可用时静默忽略
    }
  }

  /**
   * 构造发往插件（Worker/iframe）的初始化数据：当前主题、语言、占位用户信息、
   * 权限列表，以及通过 {@link collectCssVariables} 收集的宿主 CSS 变量快照。
   */
  private createInitPayload(pluginId: string): InitPayload {
    const manifest = this.manifests.get(pluginId);
    return {
      pluginId,
      theme: document.documentElement.classList.contains("dark") ? "dark" : "light",
      language: document.documentElement.lang || "zh-CN",
      user: { id: "", name: "" },
      permissions: manifest?.permissions ?? [],
      cssVariables: collectCssVariables(),
    };
  }
}
